import React, { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { institutionItemSelectors } from '@ucheba/store/institutions/item'
import { Heading } from '@ucheba/ui/components/Heading/desktop'
import { Navigation } from '@ucheba/ui/components/Navigation/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'
import { Container } from '@ucheba/ui/components/Container/desktop'
import { usePagination } from '@ucheba/utils/hooks/usePagination'
import { Text } from '@ucheba/ui/components/Text/desktop'
import { Pagination } from '@ucheba/ui/components/Pagination/desktop'
import { institutionProgramsLimit } from '@ucheba/store/institutions/programs/constants'
import useFacetsSubmit from '@ucheba/utils/hooks/useFacetsSubmit'
import { institutionsProgramsSelectors } from '@ucheba/store/institutions/programs'
import Loader from '@ucheba/ui/components/Loader/desktop/Loader'
import { ELoading } from '@ucheba/store/utils/response/types'
import { p11n } from '@ucheba/utils/helpers/string'
import { Banner } from '../../../../../../components/Banner/desktop'
import ProgramsPageFacets from './ProgramsPageFacets'
import { useFacetsKeys, useProgramsList, useProgramsPageCore } from '../bll'
import Programs from '../../../../../../components/Programs/desktop'
import { IProgramsPageProps } from '../types'
import ProgramsPageSort from './ProgramsPageSort'
import ProgramsPageSearch from './ProgramsPageSearch'
import LayoutInstitution from '../../../../../../components/LayoutInstitution/desktop'
import SelectedFacetsChips from '../../../../../../components/SelectedFacetsChips/desktop/SelectedFacetsChips'
import EmptySearch from '../../../../../../components/EmptySearch/desktop/EmptySearch'
import { isPaidPackage } from '../../../../ssr/[id]/utils/isPaidPackage'

const ProgramsPage: FC<IProgramsPageProps> = () => {
  const data = useSelector(institutionItemSelectors.currentInstitution)
  const loadingPrograms = useSelector(institutionsProgramsSelectors.loading)

  const { items, activeIndex, programType, title } = useProgramsPageCore(data.id)

  const { programs, total, facets } = useProgramsList(data.id, programType)

  const { totalPages, setCurrentPage, currentPage } = usePagination(
    total,
    institutionProgramsLimit
  )

  const { onSubmit } = useFacetsSubmit()

  const keys = useFacetsKeys(facets)

  const { paidPackage } = useSelector(institutionItemSelectors.currentInstitution) || {}

  const extraParams = useMemo(
    () => ({
      'programTypes[]': [programType],
    }),
    [programType]
  )

  return (
    <>
      <LayoutInstitution
        data={data}
        showRepresentative={false}
        showButtons={false}
        showEducationalInstitutionAddedNotification={false}
      />

      <Container>
        <Heading as='h1' h2={title} spacing='medium' />

        <Navigation items={items} activeIndex={activeIndex} />

        <Spacing spacing='xlarge' />

        <ProgramsPageSearch onSubmit={onSubmit} />

        <Spacing spacing='xlarge' />

        <div>
          <ProgramsPageSort programType={programType} />
        </div>

        <div>
          <SelectedFacetsChips facets={keys} />
        </div>

        <Grid>
          <GridCol uxs={18}>
            {loadingPrograms === ELoading.pending ? (
              <div
                style={{
                  minHeight: '100vh',
                }}
              >
                <Loader />
              </div>
            ) : (
              <>
                {programs?.length === 0 && loadingPrograms !== ELoading.idle ? (
                  <>
                    <Spacing spacing='xlarge' />

                    <EmptySearch />
                  </>
                ) : (
                  <>
                    <Programs
                      linkTarget={null}
                      isBanner={!isPaidPackage(data.paidPackage)}
                      items={programs}
                      programType={programType}
                    />

                    <Spacing spacing='xlarge' />

                    <Grid justifyContent='space-between' alignItems='center'>
                      <GridCol width='auto'>
                        <Text fontWeight='bold'>
                          {`${total} ${p11n(
                            total || 0,
                            'программа',
                            'программы',
                            'программ'
                          )}`}
                        </Text>
                      </GridCol>

                      <GridCol width='auto'>
                        <Pagination
                          totalPages={totalPages}
                          currentPage={currentPage}
                          changePageHandler={setCurrentPage}
                        />
                      </GridCol>
                    </Grid>
                  </>
                )}
              </>
            )}
          </GridCol>

          <GridCol uxs={6}>
            <div
              style={{
                paddingLeft: '32px',
              }}
            >
              {programs && programs?.length > 0 && (
                <ProgramsPageFacets
                  facets={facets}
                  onSubmit={onSubmit}
                  programType={programType}
                />
              )}
            </div>
          </GridCol>
        </Grid>

        <Spacing spacing='uxlarge' />
      </Container>

      {!isPaidPackage(paidPackage) && <Banner id={6} extraParams={extraParams} />}

      {!isPaidPackage(paidPackage) && <Banner id={14} extraParams={extraParams} />}
    </>
  )
}

export default ProgramsPage
